import api from "./api";

export const fetchData = async (endpoint, params = {}) => {
  try {
    console.log("Fetching from:", endpoint, "with params:", params);

    // Get authentication token from localStorage
    const token = localStorage.getItem("authToken");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await api.get(`/${endpoint}`, {
      params,
      headers,
      validateStatus: function (status) {
        return status < 500; // Handle any status less than 500
      },
    });

    if (response.status === 404) {
      console.warn(`Endpoint /${endpoint} not found`);
      return { success: false, error: "Resource not found" };
    }

    if (!response.data) {
      console.error("No data in response");
      return { success: false, error: "No data received" };
    }

    // console.log("Response data:", response.data);
    return response.data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    // Return a structured error response instead of throwing
    return {
      success: false,
      error:
        error.response?.data?.message || error.message || "An error occurred",
      status: error.response?.status,
    };
  }
};

export const postData = async (endpoint, data) => {
  try {
    // Get authentication token from localStorage
    const token = localStorage.getItem("authToken");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await api.post(`/${endpoint}`, data, { headers });
    return response.data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const putData = async (endpoint, data) => {
  try {
    // Get authentication token from localStorage
    const token = localStorage.getItem("authToken");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await api.put(`/${endpoint}`, data, { headers });
    return response.data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const deleteData = async (endpoint) => {
  try {
    // Get authentication token from localStorage
    const token = localStorage.getItem("authToken");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await api.delete(`/${endpoint}`, { headers });
    return response.data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const uploadFile = async (file, fileType = "image") => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "path",
      fileType === "image" ? "uploads/images" : "uploads/documents",
    );

    const response = await api.post("/api/admin/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    if (!response.data || !response.data.data) {
      throw new Error("No file URL received from server");
    }

    return response.data.data;
  } catch (error) {
    console.error("Upload Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw new Error(
      error.response?.data?.message || error.message || "Upload failed",
    );
  }
};

export const createEvent = async (eventData) => {
  try {
    // Ensure we're using 'markets' instead of 'sub_markets' for consistency with API
    if (eventData.markets) {
      // Make sure all required fields are present for each market
      eventData.markets = eventData.markets.map((market) => ({
        ...market,
        // Ensure market_image is set (fallback to event_image if missing)
        market_image:
          market.market_image || market.event_image || eventData.event_image,
        // Ensure these values are numbers
        settlement_time: Number(market.settlement_time || 24),
        yes_bids: Number(market.yes_bids || 0),
        yes_asks: Number(market.yes_asks || 0),
        volume: Number(market.volume || 0),
        dollar_volume: Number(market.dollar_volume || 0),
      }));
    }

    console.log("Sending event data to API:", JSON.stringify(eventData));
    const response = await api.post("/api/admin/createevents", eventData);
    return response.data;
  } catch (error) {
    console.error("Create Event Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const createAdmin = async (adminData) => {
  try {
    const response = await api.post("api/v2/admin/auth/create", adminData);
    return response.data;
  } catch (error) {
    console.error("Create Admin Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const loginAdmin = async (credentials) => {
  try {
    const response = await api.post("api/v2/admin/auth/login", credentials);
    console.log("response", response);
    return response.data;
  } catch (error) {
    console.error("Admin Login Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const fetchAdmins = async () => {
  try {
    const response = await api.get("api/v2/admin/auth/admins");
    return response?.data;
  } catch (error) {
    console.error("Fetch Admins Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const fetchAdminById = async (id) => {
  try {
    const response = await api.get(`api/v2/admin/auth/admin/${id}`);
    return response.data;
  } catch (error) {
    console.error("Fetch Admin Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const updateAdmin = async (id) => {
  try {
    const response = await api.put(`api/v2/admin/auth/admin/${id}`);
    return response.data;
  } catch (error) {
    console.error("Update Admin Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const updateAdminPassword = async (id, passwordData) => {
  try {
    const response = await api.put(
      `api/v2/admin/auth/admin/${id}`,
      passwordData,
    );
    return response.data;
  } catch (error) {
    console.error("Update Admin Password Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

// Region API Functions
export const fetchRegions = async () => {
  try {
    const response = await api.get("/api/admin/regions");
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: error.message };
  }
};

export const createRegion = async (regionData) => {
  try {
    const response = await api.post("/api/admin/createRegion", regionData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: error.message };
  }
};

export const updateRegion = async (regionData) => {
  try {
    const response = await api.post(`/api/admin/updateRegion`, regionData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: error.message };
  }
};

export const deleteRegion = async (id) => {
  try {
    const response = await api.delete(`/api/admin/deleteRegion/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: error.message };
  }
};

// League API Functions
export const fetchLeagueById = async (id) => {
  try {
    const response = await api.get(`/api/admin/getLeaguesByid/${id}`);
    return response.data;
  } catch (error) {
    console.error("Fetch League Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error.response?.data || { success: false, message: error.message };
  }
};

export const updateLeague = async (id, leagueData) => {
  try {
    console.log("Updating league with data:", leagueData);
    const response = await api.put(`/leagues/${id}`, leagueData);
    return response.data;
  } catch (error) {
    console.error("Update League Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error.response?.data || { success: false, message: error.message };
  }
};

export const uploadLeagueImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("path", "youlft/images");

    const response = await api.post("/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (!response.data || !response.data.fileUrl) {
      throw new Error("No file URL received from server");
    }

    return response.data.fileUrl;
  } catch (error) {
    console.error("Upload Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw new Error(
      error.response?.data?.message || error.message || "Upload failed",
    );
  }
};

// Market Maker API Functions
export const startMarketMaker = async (marketMakerData) => {
  try {
    console.log("Starting market maker with data:", marketMakerData);
    const response = await api.post(
      "api/admin/market-maker/start",
      marketMakerData,
    );
    return response.data;
  } catch (error) {
    console.error("Start Market Maker Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const fetchMarketMakerStatus = async () => {
  try {
    const response = await api.get("api/admin/market-maker/status");
    return response.data;
  } catch (error) {
    console.error("Fetch Market Maker Status Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error.response?.data || { success: false, message: error.message };
  }
};

export const stopMarketMaker = async (marketId) => {
  try {
    const response = await api.delete(
      `api/admin/market-maker/stop/${marketId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Stop Market Maker Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const fetchEvents = async () => {
  try {
    const response = await api.get("api/event/events");
    return response.data;
  } catch (error) {
    console.error("Fetch Events Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error.response?.data || { success: false, message: error.message };
  }
};

export const fetchEventById = async (eventId) => {
  try {
    const response = await api.get(`api/event/events/${eventId}`);
    return response.data;
  } catch (error) {
    console.error("Fetch Event By ID Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error.response?.data || { success: false, message: error.message };
  }
};

export const fetchMarketsByEvent = async (eventId) => {
  try {
    const response = await api.get(`/events/${eventId}/markets`);
    return response.data;
  } catch (error) {
    console.error("Fetch Markets Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error.response?.data || { success: false, message: error.message };
  }
};

// Generic update function for any endpoint
export const updateData = async (endpoint, data) => {
  try {
    console.log(`Updating ${endpoint} with data:`, data);

    // Get authentication token from localStorage
    const token = localStorage.getItem("authToken");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await api.put(`/${endpoint}`, data, { headers });

    if (!response.data) {
      throw new Error("No data received from server");
    }

    return response.data;
  } catch (error) {
    console.error(`Update Error (${endpoint}):`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error.response?.data || { success: false, message: error.message };
  }
};

//// new

export const getPacksApi = async (id) => {
  try {
    const response = await api.get(`api/v2/admin/auth/packs`);
    return response.data;
  } catch (error) {
    console.error("Fetch Admin Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const createPack = async (formData) => {
  try {
    const response = await api.post("api/v2/admin/auth/packs", formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Create Pack Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const fetchPackById = async (id) => {
  try {
    const response = await api.get(`api/v2/admin/auth/packs/${id}`);
    return response.data;
  } catch (error) {
    console.error("Fetch Admin Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const updatePackApi = async (formData) => {
  try {
    const response = await api.put("api/v2/admin/auth/packs", formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Update packs Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const getItemsApi = async (id) => {
  try {
    const response = await api.get(`api/v2/admin/auth/items`);
    return response.data;
  } catch (error) {
    console.error("Fetch Admin Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const fetchItemById = async (id) => {
  try {
    const response = await api.get(`api/v2/admin/auth/items/${id}`);
    return response.data;
  } catch (error) {
    console.error("Fetch Admin Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const addPackProducetApi = async (formData) => {
  try {
    const token = localStorage.getItem("token");
    const response = await api.post("api/v2/admin/auth/items", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error.response?.data || error.message);
    throw error;
  }
};

export const updateItemApi = async (formData) => {
  try {
    const response = await api.put("api/v2/admin/auth/items", formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Update packs Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

export const updateUserApi = async (id, formData) => {
  try {
    const response = await api.put(`api/v2/admin/auth/users/${id}`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Update packs Error:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};
